import { useTranslation } from 'react-i18next';
import { useUserRoles } from '@anytag/auth';
import { ColumnDef } from '@tanstack/react-table';
import { ReadPackageAccount } from '@anytag/graphql';
import { formatIntNumber } from '@anytag/shared-utils';
import { useTranslateCountry } from '@anytag/shared-hooks';
import { Button, TableCell } from '@anytag/shared-atoms';
import { WidgetSelectButton } from '../../shared';
import { useInfluencerPackagesContext } from '../InfluencerPackagesContext';

export const useInfluencerPackagesTable = () => {
  const { t } = useTranslation();
  const { tc } = useTranslateCountry();
  const { isAdminStaff } = useUserRoles();
  const { getListSelectButtonProps } = useInfluencerPackagesContext();

  const columns: ColumnDef<ReadPackageAccount>[] = [
    {
      size: 40,
      header: '',
      accessorKey: 'add',
      meta: { sticky: true },
      cell: ({ row }) => <WidgetSelectButton {...getListSelectButtonProps?.(row.original)} />
    },
    {
      accessorKey: 'package',
      meta: { sticky: true },
      header: t<string>('HeaderColumn.Package'),
      cell: ({ row }) => (
        <TableCell.Link
          variant="blue"
          target="_blank"
          className="min-w-55"
          label={row.original.name}
          to="/package/$id/content"
          params={{ id: row.original.id.toString() }}
        />
      )
    },
    {
      size: 500, // take all the space
      accessorKey: 'company',
      meta: { hidden: !isAdminStaff },
      header: t<string>('HeaderColumn.Company'),
      cell: ({ row }) => {
        const { companies } = row.original;

        return (
          <TableCell.Text
            lines={2}
            value={companies?.map((el) => el?.name).join(', ') || '-'}
            tooltip={{
              help: companies?.length ? (
                <ul>{companies?.map((company) => <li key={company?.id}>{company?.name}</li>)}</ul>
              ) : null
            }}
          />
        );
      }
    },
    {
      accessorKey: 'country',
      header: t<string>('HeaderColumn.Country'),
      cell: ({ row }) => <TableCell.Text value={tc(row.original.country.name)} />
    },
    {
      accessorKey: 'account',
      meta: { align: 'right' },
      header: t<string>('HeaderColumn.Account'),
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.accounts || 0)} />
    },
    {
      accessorKey: 'toggle',
      header: '',
      size: 40,
      cell: ({ row }) => (
        <Button
          variant="outline"
          className="ml-auto h-6 min-w-6 p-0"
          onClick={row.getToggleExpandedHandler()}
          prefixIcon={{ icon: row.getIsExpanded() ? 'caret-up' : 'caret-down', size: '8px' }}
        />
      )
    }
  ];

  return { columns };
};
